// FILE: ui/services/todosSummary.ts
// 儀錶板待辦彙總（即時計算）

import {
  db,
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
} from "../../firebaseClient";
import { ApprovalRequest } from "../types/legalOps";
import {
  isApprovalPendingForUser,
  SummaryUserKey,
  getPendingUserKeysFromFlow,
} from "../utils/approvalHelpers";
import { getRandomWarmTip } from "../utils/getRandomWarmTip";
import {
  DailySummaryResult,
  UserRealtimeSummary,
  PendingItem,
  PendingGrouped,
  PendingModule,
  emptyPendingGrouped,
  computeCounts,
  buildEmptyUser,
} from "./dailySummaryService";
import { TrueTodoItem } from "../types/trueTodo";

// -----------------------------
// Helpers
// -----------------------------

function normalizeUserDisplayName(k: SummaryUserKey): string {
  if (k === "kevin") return "法務";
  if (k === "ops") return "營運長";
  return "總經理";
}

function classifyModule(raw: any): PendingModule | null {
  const v = String(raw || "").trim();
  const upper = v.toUpperCase();

  // 1) 合約會審
  if (
    upper === "CONTRACT_REVIEW" ||
    upper === "CONTRACT" ||
    upper.includes("CONTRACT_REVIEW")
  ) {
    return "contractReviews";
  }

  // 2) 範本
  if (upper.startsWith("TEMPLATE") || upper.includes("TEMPLATES")) {
    return "templates";
  }

  // 3) 用印
  if (upper === "SEAL_REQUEST" || upper.includes("SEAL")) {
    return "seals";
  }

  // 4) 公文函覆
  if (
    upper === "OFFICIAL_REPLY" ||
    upper === "GOV_CORRESPONDENCE" ||
    upper.includes("GOV_CORRESP") ||
    upper.includes("OFFICIAL")
  ) {
    return "govCorrespondence";
  }

  // 5) 法遵情資
  if (upper === "LAW_INFO" || upper.includes("LAW_INFO") || upper.includes("INTEL")) {
    return "regIntel";
  }

  // 6) 專案管控
  if (upper === "GOV_PROJECT" || upper.includes("GOV_PROJECT") || upper.includes("PROJECT")) {
    return "projectControl";
  }

  // 7) 核心文件工作區
  if (
    upper === "CORE_DOC" ||
    upper === "GOVERNANCE_CORE_DOC" ||
    upper.includes("CORE_DOC") ||
    upper.includes("GOVERNANCE_CORE")
  ) {
    return "governanceWorkspace";
  }

  // 8) 案件與爭議（僅在明確標示 CASE/DISPUTE/LITIGATION 時才算）
  if (
    upper === "CASES" ||
    upper === "CASE" ||
    upper.includes("CASE") ||
    upper.includes("DISPUTE") ||
    upper.includes("LITIGATION")
  ) {
    return "cases";
  }

  // 9) 其他/未知：不計入儀錶板待辦（避免幽靈待辦）
  return null;
}

function pushToGrouped(p: PendingGrouped, item: PendingItem) {
  (p[item.module] ||= []).push(item);
}

function baseResult(dateLabel: string): DailySummaryResult {
  const users: UserRealtimeSummary[] = [
    buildEmptyUser("kevin", normalizeUserDisplayName("kevin"), dateLabel),
    buildEmptyUser("ops", normalizeUserDisplayName("ops"), dateLabel),
    buildEmptyUser("gm", normalizeUserDisplayName("gm"), dateLabel),
  ];

  return {
    dateKey: dateLabel,
    dateLabel,
    generatedAt: new Date().toISOString(),
    users,
    dueSoon: { today: [], within30: [], within60: [] },
    warmTip: getRandomWarmTip(),
    debug: { schemaVersion: "live" },
  };
}

// -----------------------------
// computeDashboardSummary
// - legacy todos（todos collection）
// - approval_requests（簽核待辦）
// -----------------------------

export async function computeDashboardSummary(date?: Date): Promise<DailySummaryResult> {
  const dateLabel = formatDate(date || new Date());
  const res = baseResult(dateLabel);

  // 使用 map 方便累積
  const byUser: Record<SummaryUserKey, UserRealtimeSummary> = {
    kevin: res.users[0],
    ops: res.users[1],
    gm: res.users[2],
  };

  try {
    // 1) Legacy Todos（案件/爭議、內控、臨時待辦）
    const todosQ = query(
      collection(db as any, "todos"),
      where("status", "in", ["OPEN", "PENDING", "IN_PROGRESS"])
    );
    const todosSnap = await getDocs(todosQ as any);
    todosSnap.forEach((d: any) => {
      const todo = d.data?.() || d.data || {};
      const assignee = String(todo.assigneeUserKey || todo.assignee || "").toLowerCase();
      const userKey = (assignee === "kevin" ? "kevin" : assignee === "ops" ? "ops" : assignee === "gm" ? "gm" : null) as SummaryUserKey | null;
      if (!userKey) return;

      const module = classifyModule(todo.module || todo.moduleKey || todo.sourceType);
      if (!module) return;
      const item: any = {
        id: d.id,
        title: String(todo.title || todo.name || todo.subject || "(未命名待辦)"),
        module,
        source: "todos",
        rawId: d.id,
        moduleKey: todo.module || todo.moduleKey,
        status: todo.status,
      };
      pushToGrouped(byUser[userKey].pendingList, item);
    });

    // 2) approval_requests：依 flow + stage 判斷誰的待辦
    const approvalsQ = query(
      collection(db as any, "approval_requests"),
      where("status", "==", "PENDING")
    );
    const approvalsSnap = await getDocs(approvalsQ as any);

    for (const d of approvalsSnap.docs as any[]) {
      const req = (d.data?.() || d.data || {}) as ApprovalRequest;
      const reqAny: any = req as any;

      // 取得 flow（用 flowId / approvalFlowId）
      let flow: any = null;
      const flowId =
        reqAny.approvalFlowId ??
        reqAny.approvalFlowID ??
        reqAny.flowId ??
        reqAny.flowID ??
        reqAny.flow_id ??
        null;

      if (flowId) {
        const flowRef = doc(db as any, "approval_flows", String(flowId));
        const flowSnap = await getDoc(flowRef as any);
        if (flowSnap.exists()) flow = flowSnap.data();
      }

      // 依 userKey 判斷是否 pending
      (Object.keys(byUser) as SummaryUserKey[]).forEach((userKey) => {
        // isApprovalPendingForUser(request, userKey, flow?)
        if (!isApprovalPendingForUser(req as any, userKey as any, flow as any)) return;

        const module = classifyModule(
          reqAny.module ??
            reqAny.moduleKey ??
            reqAny.approvalModule ??
            reqAny.approvalModuleKey ??
            reqAny.approvalModuleId ??
            reqAny.approvalModuleID ??
            reqAny.approvalModuleCode ??
            ""
        );
        if (!module) return;
        const item: any = {
          id: d.id,
          title: String(
            reqAny.title ||
              reqAny.subject ||
              reqAny.name ||
              reqAny.docNo ||
              reqAny.requestNo ||
              "(簽核案件)"
          ),
          module,
          source: "approval_requests",
          rawId: d.id,
          docNo: String(reqAny.docNo || reqAny.requestNo || ""),
          moduleKey: String(reqAny.module ?? reqAny.moduleKey ?? reqAny.approvalModule ?? ""),
          status: String(reqAny.status || "PENDING"),
          stageKey: String((flow as any)?.currentStageKey || ""),
          targetUserKeys: (getPendingUserKeysFromFlow(flow as any) as any) || undefined,
        };
        pushToGrouped(byUser[userKey].pendingList, item);
      });
    }

    // counts
    res.users = (Object.keys(byUser) as SummaryUserKey[]).map((k) => {
      const u = byUser[k];
      u.counts = computeCounts(u.pendingList);
      return u;
    });

    return res;
  } catch (e) {
    // 任何錯誤回傳基本空資料，避免整頁掛掉
    return res;
  }
}

// -----------------------------
// computeTrueDashboardSummary
// - 使用 TrueTodoItem（即時計算後的「真實待辦」）
// -----------------------------

export function computeTrueDashboardSummary(trueTodoList: TrueTodoItem[], date?: Date): DailySummaryResult {
  const dateLabel = formatDate(date || new Date());
  const res = baseResult(dateLabel);

  const byUser: Record<SummaryUserKey, UserRealtimeSummary> = {
    kevin: res.users[0],
    ops: res.users[1],
    gm: res.users[2],
  };

  for (const t of trueTodoList || []) {
    // 只計入「簽核流轉」；RAW_TODO 常包含舊檔/測試檔/已刪檔索引，會造成幽靈待辦
    if (String((t as any).source || "") === "RAW_TODO") continue;
    // 依 targetUsers 分配：TrueTodoAdapter 會輸出 [{ userKey: "kevin" | "ops" | "gm" }, ...]
    // 但舊版這裡把它當成 string[]，導致整批 item 都分派不到任何人 → 三卡全 0。
    const rawTargets = Array.isArray((t as any).targetUsers) ? (t as any).targetUsers : [];

    const targetKeys: SummaryUserKey[] = Array.from(
      new Set(
        rawTargets
          .map((x: any) => {
            // 支援兩種格式："kevin" 或 { userKey: "kevin" }
            if (typeof x === "string") return x;
            if (x && typeof x === "object") return x.userKey;
            return null;
          })
          .map((k: any) => String(k || "").trim().toLowerCase())
          .map((k: string) => (k === "kevin" || k === "ops" || k === "gm" ? (k as SummaryUserKey) : null))
          .filter(Boolean) as SummaryUserKey[]
      )
    );

    // 若沒有 targetUsers，視為不完整資料：不計入儀錶板（避免幽靈待辦）
    if (!targetKeys.length) continue;
    const effectiveTargets: SummaryUserKey[] = targetKeys;

    const module = classifyModule((t as any).module || (t as any).moduleKey || (t as any).sourceModule);
    if (!module) continue;
    const item: any = {
      id: String((t as any).id || (t as any).rawId || (t as any).docNo || ""),
      title: String((t as any).title || (t as any).name || (t as any).docNo || "(待辦)"),
      module,
      source: String((t as any).source || "trueTodo"),
      rawId: String((t as any).rawId || ""),
      docNo: String((t as any).docNo || ""),
      moduleKey: String((t as any).module || (t as any).moduleKey || ""),
      status: String((t as any).status || ""),
      stageKey: String((t as any).stageKey || ""),
      targetUserKeys: effectiveTargets as any,
      // keep raw timestamps for KPI aging (Shell.tsx uses p.raw / p.updatedAt / p.createdAt)
      raw: (t as any).raw,
      createdAt: (t as any).createdAt,
      updatedAt: (t as any).updatedAt,
    };

    for (const uk of effectiveTargets) {
      if (!byUser[uk]) continue;
      pushToGrouped(byUser[uk].pendingList, item);
    }
  }

  // counts
  res.users = (Object.keys(byUser) as SummaryUserKey[]).map((k) => {
    const u = byUser[k];
    u.counts = computeCounts(u.pendingList);
    return u;
  });

  return res;
}

// --- local util ---
function formatDate(d: Date): string {
  // 這裡避免與其他 util 互相依賴：輸出 YYYY-MM-DD
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
